import { reactive, watch, toRefs, nextTick, ref } from 'vue';
import { useScriptTag, useLocalStorage, useEventListener } from '@vueuse/core';

const CONWORD_CONFIG = {
  disable_rtl_attribute: false,
  disable_language_switcher: true,
};

const _state = reactive({
  isActive: false,
  languageCode: document.documentElement.lang || 'de',
});

const conwordConfig = useLocalStorage('cnw_config');

if (conwordConfig.value) {
  try {
    _state.languageCode = JSON.parse(conwordConfig.value).user_language;
    _state.isActive = true;
  } catch (error) {
    console.warn('Failed to parse conword config from localStorage', error);
  }
}

const { load, unload } = useScriptTag(
  'https://static.conword.io/js/v2/dresden_philharmonie/conword.js',
  element => {
    element.id = 'conword-root';
    // do something
  },
  { manual: true }
);

const { isActive, languageCode } = toRefs(_state);
const timeoutId = ref(null);

const enable = async () => {
  if (window.Conword) return;

  window.conword_config = CONWORD_CONFIG;
  await load(true);
  isActive.value = true;
  console.log('Conword enabled');
};

const disable = async () => {
  if (!window.Conword) return;

  window.Conword.unset_user_language();
  await unload();
  localStorage.removeItem('cnw_config');
  conwordConfig.value = null;
  lastUpdateTimestamp.value = null;
  isActive.value = false;
  console.log('Conword disabled');
};

watch(
  isActive,
  isActive => {
    isActive ? enable() : disable();
  },
  { immediate: true }
);
console.log('INIT CONWORD');

export function useConword(config) {
  const { isActive, languageCode } = toRefs(_state);

  watch(languageCode, async languageCode => {
    if (!languageCode) return;

    if (config?.disabledLanguageCodeList?.includes(languageCode)) {
      console.log('Disabling translation for ', languageCode);
      window.Conword?.translate(languageCode);
      return disable();
    }

    if (!window.Conword) {
      await enable();
    }

    console.log('Setting language to ', languageCode);
    window.Conword.translate(languageCode);

    clearTimeout(timeoutId.value);
    timeoutId.value = setTimeout(() => {
      if (document.documentElement.lang !== languageCode) {
        console.log('Retrying translation for ', languageCode);
        window.Conword.translate(languageCode);
      }
    }, 3000);
  });

  return {
    enable,
    disable,
    languageCode,
    isActive,
  };
}
